/**
 * Kleda Client App - Vlekkenbaas Upsell
 * Professionele vlekverwijderaar met vlekkendoekje
 *
 * Getoond wanneer:
 * - Status = 'Terugbrengen plannen', 'Aflevervoorstel gedaan' of 'Terugbrengen ingepland'
 * - Vlekkenbaas nog niet besteld
 *
 * Prijs: €17,50 per set incl. BTW (via Prijsmanagement__c)
 * Productcode: WAS-UPSELL-KLEDA-VLEKVERWIJDERAAR
 * Klant kan 1-5 sets bestellen
 */

import type { OrderData } from '../types'
import { useVlekkenbaas } from '../hooks'
import { UpsellCard } from './UpsellCard'

// =============================================================================
// TYPES
// =============================================================================

interface VlekkenbaasUpsellProps {
  order: OrderData
  token: string
  highlighted?: boolean
  compact?: boolean
  /** Toon ongeacht status — voor het post-bevestiging-moment (afspraak staat dan al op 'ingepland') */
  forceShow?: boolean
  onSuccess: () => void
}

// =============================================================================
// DELIVERY STATUSES WHERE VLEKKENBAAS IS SHOWN
// =============================================================================

// Alleen in de kies-fase: daar is de upsell het enige secundaire element.
// Op de gepland-pagina is betalen de enige open taak — geen concurrentie.
const VLEKKENBAAS_STATUSES = [
  'Terugbrengen plannen',
  'Aflevervoorstel gedaan',
]

// =============================================================================
// HELPERS
// =============================================================================

function formatPrice(amount: number): string {
  return `€ ${amount.toFixed(2).replace('.', ',')}`
}

/**
 * Valt er voor deze order nog een Vlekkenbaas aan te bieden?
 *
 * Eén bron van waarheid voor "is er iets te kiezen", zodat een aanroeper die
 * het moment inricht (post-bevestiging-stap) dezelfde grens hanteert als de
 * kaart zelf. Zonder deze deling toonde de bevestigingspagina een volledig
 * scherm rond een kaart die alleen nog "1x toegevoegd!" kon zeggen — een dode
 * stap voor de klant die de upsell al in zijn order had (20-08, WB-322242).
 *
 * De statuscheck zit hier bewust NIET in: `forceShow` bestaat juist omdat de
 * status vlak na het bevestigen al is omgeklapt.
 */
export function vlekkenbaasTeBieden(order: OrderData): boolean {
  const alBesteld =
    order.vlekkenbaas?.alreadyOrdered === true ||
    (order.items?.some(item => item.productCode === 'WAS-UPSELL-KLEDA-VLEKVERWIJDERAAR') ?? false)
  if (alBesteld) return false
  // Backend beslist zodra het blok meekomt; ontbreekt het, dan frontend-driven
  // tonen (zelfde terugval als `showCard` hieronder).
  return order.vlekkenbaas ? order.vlekkenbaas.eligible === true : true
}

// =============================================================================
// COMPONENT
// =============================================================================

export function VlekkenbaasUpsell({ order, token, highlighted = false, compact = false, forceShow = false, onSuccess }: VlekkenbaasUpsellProps) {
  const {
    isEligible,
    isOrdered,
    orderedQuantity,
    unitPrice,
    quantity,
    totalPrice,
    setQuantity,
    submit,
    isSubmitting,
    error,
    success
  } = useVlekkenbaas(token, order, onSuccess)

  // Don't show if not in delivery phase
  if (!forceShow && !VLEKKENBAAS_STATUSES.includes(order.status)) {
    return null
  }

  // Reden-waarom personaliseren met wat we al van dit kleed weten:
  // inspectie zag vlekken > huisdier-pakket > generiek
  const heeftVlekken = order.voorinspectie?.infoEnkeleVlekken === true
  const heeftHuisdier = order.items?.some(i => i.productNaam?.toLowerCase().includes('huisdier')) ?? false
  const description = heeftVlekken
    ? 'Bij de inspectie zagen we een paar vlekken — met de Vlekkenbaas pak je nieuwe vlekken voortaan direct zelf aan. Professionele vlekverwijderaar met vlekkendoekje.'
    : heeftHuisdier
      ? 'Met een huisdier in huis is een nieuwe vlek zo gemaakt — met de Vlekkenbaas pak je \'m direct zelf aan. Professionele vlekverwijderaar met vlekkendoekje.'
      : 'Professionele vlekverwijderaar met vlekkendoekje. Speciaal voor vloerkleden — handig voor toekomstige vlekken.'

  // Also check items for vlekkenbaas product (fallback when vlekkenbaas data is missing after refetch)
  const orderedViaItems = order.items?.some(
    item => item.productCode === 'WAS-UPSELL-KLEDA-VLEKVERWIJDERAAR'
  ) ?? false

  // Don't show if not eligible (backend decides)
  // If vlekkenbaas data is not present, show with default price (frontend-driven)
  const showCard = order.vlekkenbaas
    ? isEligible && !isOrdered
    : !isOrdered && !orderedViaItems

  if (!showCard && !isOrdered && !orderedViaItems && !success) {
    return null
  }

  const formattedPrice = formatPrice(totalPrice)
  const showQuantityHint = quantity > 1

  return (
    <UpsellCard
      variant="vlekkenbaas"
      status={success || isOrdered || orderedViaItems ? 'accepted' : 'available'}
      werkOrderId={order.werkOrderId}
      headline="Vlekkenbaas meenemen?"
      description={description}
      price={formattedPrice}
      priceSubtext={showQuantityHint ? `${quantity}x ${formatPrice(unitPrice)}` : undefined}
      benefits={[
        'Verwijdert de meeste vlekken direct',
        'Handige spray + vlekkendoekje',
        'Bij aflevering meegegeven met je kleed',
      ]}
      ctaLabel={quantity > 1 ? `${quantity} sets meenemen` : 'Meenemen bij aflevering'}
      badge="Populair"
      imageUrl="/images/vlekkenbaas.jpg"
      highlighted={highlighted}
      compact={compact}
      isSubmitting={isSubmitting}
      error={error}
      onAccept={submit}
      quantity={quantity}
      onQuantityChange={setQuantity}
      maxQuantity={5}
      acceptedMessage={
        (isOrdered || orderedViaItems)
          ? `${orderedQuantity || 1}x Vlekkenbaas toegevoegd!`
          : success
            ? `${quantity}x Vlekkenbaas toegevoegd!`
            : undefined
      }
    />
  )
}
